import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary компонент для ловли ошибок React
 *
 * Использование:
 * ```tsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * С кастомным fallback:
 * ```tsx
 * <ErrorBoundary fallback={<CustomErrorPage />}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Логируем ошибку
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // Можно отправить ошибку в систему мониторинга
    // sendErrorToMonitoring(error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoBack = (): void => {
    // Попробуем использовать Telegram WebApp для закрытия
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.close();
    } else {
      window.history.back();
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Если есть кастомный fallback - используем его
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Дефолтный UI ошибки
      return (
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.icon}>😔</div>
            <h2 style={styles.title}>Что-то пошло не так</h2>
            <p style={styles.message}>
              Произошла неожиданная ошибка. Попробуйте перезагрузить страницу.
            </p>

            {process.env.REACT_APP_DEBUG === 'true' && this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Техническая информация</summary>
                <pre style={styles.errorText}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={styles.buttons}>
              <button onClick={this.handleReload} style={styles.primaryButton}>
                Перезагрузить
              </button>
              <button onClick={this.handleGoBack} style={styles.secondaryButton}>
                Вернуться назад
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Стили (inline для простоты, можно вынести в CSS)
const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
    backgroundColor: 'var(--tg-theme-bg-color, #ffffff)',
    color: 'var(--tg-theme-text-color, #000000)',
  },
  content: {
    textAlign: 'center',
    maxWidth: '400px',
  },
  icon: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: '12px',
    color: 'var(--tg-theme-text-color, #000000)',
  },
  message: {
    fontSize: '16px',
    color: 'var(--tg-theme-hint-color, #999999)',
    marginBottom: '24px',
    lineHeight: 1.5,
  },
  details: {
    textAlign: 'left',
    marginBottom: '24px',
    padding: '12px',
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #f5f5f5)',
    borderRadius: '8px',
  },
  summary: {
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '8px',
  },
  errorText: {
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#e53935',
    maxHeight: '200px',
    overflow: 'auto',
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  primaryButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 500,
    color: '#ffffff',
    backgroundColor: 'var(--tg-theme-button-color, #3390ec)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 500,
    color: 'var(--tg-theme-button-color, #3390ec)',
    backgroundColor: 'transparent',
    border: '1px solid var(--tg-theme-button-color, #3390ec)',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

export default ErrorBoundary;
